import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import Tippy from '@tippy.js/react';
import colors from 'theme/_colors.scss';
import { IconHide, IconView, IconInfo, IconDelete } from 'svgIcons/MotionPortalIcons';

import { getAssetIcon } from 'helpers/icons';
import { ContextMenu, GeneralSelect } from 'sharedComponents';

import { removeKPIData } from '../../../../PowertrainAssetDetails/CrossAssetAnalysis/ChartView/actions';
import { CHART_MAX_KPI_LIMIT, opButtons } from '../../../../PowertrainAssetDetails/CrossAssetAnalysis/ChartView/constants';
import { getNormalizedAssetId } from '../../helpers/assetIdentity';

const SignalBucketComponent = ({
    chartData,
    offGraphSignals,
    loadingOffGraphSignalKeys,
    activeTab,
    onClear,
    onHideOnGraphSignal,
    onPromoteOffGraphSignal,
    onRemoveOffGraphSignal,
    onComparisonItemClick,
    selectedComparisonAssetId,
    selectedComparisonOverrideKey,
    getComparisonItemSelectionKey,
    lockedSignalKey,
    lockedSignalSelectValue,
    lockedSignalName,
    scatterHistogramSignals,
    hasActiveSubscription,
    onChangeLockedSignal
}) => {
    const { t: translate } = useTranslation();
    const dispatch = useDispatch();
    const onGraphSignals = chartData || [];
    const signalCount = onGraphSignals.length;
    const offGraphCount = offGraphSignals?.length || 0;
    const totalSelectedSignals = signalCount + offGraphCount;
    const isEmpty = totalSelectedSignals === 0;
    const isAtLimit = signalCount >= CHART_MAX_KPI_LIMIT;
    const isScatterOrHistogram = [opButtons.histogramV2, opButtons.scatterPlotV2].includes(activeTab);
    // Falls back to the single current locked signal when the browsed asset has no signals to offer (e.g. no subscription)
    const lockedSignalOptions = scatterHistogramSignals?.length
        ? scatterHistogramSignals.map(sig => ({
            key: activeTab === opButtons.histogramV2 ? sig.histogramKey : sig.scatterKey,
            label: sig.displayName || sig.name
        }))
        : [{ key: lockedSignalKey, label: lockedSignalName }];
    const selectedLockedSignalKey = lockedSignalSelectValue || lockedSignalKey;
    const selectedLockedSignalOption = lockedSignalOptions.find(option => option.key === selectedLockedSignalKey) || lockedSignalOptions[0];

    const onGraphTooltip = translate('ABB.Powertrain.Frontend.crossAssetOnGraphTooltip');
    const offGraphTooltip = translate('ABB.Powertrain.Frontend.crossAssetOffGraphTooltip');

    const tippyProps = {
        animation: 'scale',
        theme: 'light-border',
        trigger: 'mouseenter',
        placement: 'top',
        maxWidth: 280,
        interactive: true,
        interactiveBorder: 10,
    };

    const renderInfoIcon = (tooltip) =>
        <Tippy content={<span className='signal-bucket-tooltip-text'>{tooltip}</span>} {...tippyProps} trigger='click' hideOnClick={true}>
            <span className='signal-bucket-info-icon' aria-label='info'>
                <IconInfo width={14} height={14} color={colors.doveGray} />
            </span>
        </Tippy>
        ;

    const renderItem = (item, index, group) => {
        const comparisonItemKey = getComparisonItemSelectionKey?.(item, group, index);
        const itemAssetId = getNormalizedAssetId(item?.asset) || getNormalizedAssetId(item?.component);
        // Blue highlight: only the specific entry that was clicked in the comparison set
        const isDirectlySelected = selectedComparisonOverrideKey
            ? comparisonItemKey === selectedComparisonOverrideKey
            : false;
        // Grey shift: all items from the selected asset that are NOT the directly-selected entry
        const isSameAssetGrouped = Boolean(selectedComparisonAssetId)
            && itemAssetId === selectedComparisonAssetId
            && !isDirectlySelected;
        const isOnGraph = group === 'on';
        // Signal's chart data is being fetched after clicking the eye icon
        const isOffGraphSignalLoading = !isOnGraph && Boolean(loadingOffGraphSignalKeys?.has(item?.key));

        const removeLabel = translate('ABB.Powertrain.Frontend.crossAssetRemoveFromSet');
        const itemAssetName = isOnGraph
            ? item?.component?.name || item?.component?.componentName || item?.asset?.name || item?.asset?.componentName
            : item?.asset?.name || item?.asset?.componentName || item?.kpi?.assetName;

        let offGraphViewBtn;
        if (isOffGraphSignalLoading) {
            offGraphViewBtn = <button
                className='signal-bucket-action-btn signal-bucket-action-btn--loading'
                type='button'
                onClick={(event) => event.stopPropagation()}
                disabled
                aria-label='Loading'
            >
                <IconView width={16} height={16} color={colors.doveGray} />
            </button>;
        } else if (isAtLimit) {
            offGraphViewBtn = <Tippy
                content={
                    <span>
                        {translate('ABB.Powertrain.Frontend.crossAssetMaxSignalsTooltip') ||
                            `The graph already has ${CHART_MAX_KPI_LIMIT}/${CHART_MAX_KPI_LIMIT} signals. Hide one on-graph signal to make room for this one.`}
                    </span>
                }
                trigger='click'
                hideOnClick={true}
                animation='scale'
                theme='light-border'
                placement='top'
                maxWidth={280}
                interactive={true}
                interactiveBorder={10}
            >
                <button
                    className='signal-bucket-action-btn'
                    type='button'
                    onClick={(event) => event.stopPropagation()}
                    aria-label='Graph is full'
                >
                    <IconView width={16} height={16} color={colors.doveGray} />
                </button>
            </Tippy>;
        } else {
            offGraphViewBtn = <button
                className='signal-bucket-action-btn'
                type='button'
                onClick={(event) => {
                    event.stopPropagation();
                    onPromoteOffGraphSignal?.(item);
                }}
                aria-label='Show on graph'
            >
                <IconView width={16} height={16} color={colors.doveGray} />
            </button>;
        }

        return (
            <div
                key={isOnGraph
                    ? `${item?.kpi?.timeseriesNameTextKey || item?.kpi?.histogramKey || index}-${item?.component?.id || item?.asset?.id}`
                    : item?.key}
                className={`signal-bucket-item${isOnGraph ? '' : ' signal-bucket-item--off-graph'}${isDirectlySelected ? ' selected' : ''}${isSameAssetGrouped ? ' same-asset' : ''}${isOffGraphSignalLoading ? ' signal-bucket-item--loading' : ''}`}
            >
                <div className='signal-bucket-item-header'>
                    <button
                        type='button'
                        className='signal-bucket-item-select-area'
                        onClick={() => onComparisonItemClick?.(item, group, index)}
                        disabled={isOffGraphSignalLoading}
                        aria-label={itemAssetName}
                    >
                        <span className='signal-bucket-asset-icon'>{getAssetIcon(
                            (item?.component || item?.asset)?.componentTypeName ||
                            (item?.component || item?.asset)?.assetType ||
                            (item?.component || item?.asset)?.componentType?.typeName,
                            { width: 16, height: 16 }
                        )}</span>
                        <div className='signal-bucket-signal-meta d-flex flex-column'>
                            <Tippy
                                content={itemAssetName}
                                animation='scale'
                                theme='light-border'
                                trigger='mouseenter'
                                placement='top'
                                maxWidth={280}
                                onShow={({ reference }) => reference.scrollWidth > reference.offsetWidth}
                            >
                                <span className='signal-bucket-asset-name'>{itemAssetName}</span>
                            </Tippy>
                            <span className='signal-bucket-signal-name'>
                                {item?.kpi?.displayName || item?.kpi?.name || item?.kpi?.timeseriesName || item?.kpi?.label} {item?.kpi?.unit ? `(${item?.kpi?.unit})` : ''}
                            </span>
                        </div>
                    </button>
                    <div className='signal-bucket-item-actions ms-auto'>
                        {isOnGraph
                            ? <button
                                className='signal-bucket-action-btn'
                                type='button'
                                onClick={(event) => {
                                    event.stopPropagation();
                                    onHideOnGraphSignal?.(item, index);
                                }}
                                aria-label='Hide from graph'
                            >
                                <IconHide width={16} height={16} />
                            </button>
                            : offGraphViewBtn
                        }
                        <div className='signal-bucket-menu-wrapper'>
                            <ContextMenu
                                options={[{
                                    id: 'remove',
                                    disabled: isOffGraphSignalLoading,
                                    title:
                                        <span className='signal-bucket-context-menu-label d-flex align-items-center'>
                                            <span className='signal-bucket-context-menu-icon d-inline-flex me-2'><IconDelete width={14} height={14} color={colors.mineShaft} /></span>
                                            {removeLabel}
                                        </span>,
                                    onOptionSelect: () => isOnGraph
                                        ? dispatch(removeKPIData(index))
                                        : onRemoveOffGraphSignal?.(item)
                                }]}
                            />
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className='signal-bucket'>
            <div className='signal-bucket-header'>
                <span className='signal-bucket-title'>
                    {translate('ABB.Powertrain.Frontend.crossAssetComparisonSet')}
                </span>
                <span className='signal-bucket-count'>
                    {totalSelectedSignals} {translate('ABB.Powertrain.Frontend.crossAssetSignalsLabel') || 'signal(s)'}
                </span>

                <div className={`signal-bucket-menu-wrapper${isEmpty ? ' signal-bucket-header-context-menu--disabled' : ''}`}>
                    <ContextMenu
                        options={[{
                            id: 'clear-all',
                            title:
                                <span className='signal-bucket-context-menu-label d-flex align-items-center'>
                                    <span className='signal-bucket-context-menu-icon d-inline-flex me-2'><IconDelete width={14} height={14} color={colors.mineShaft} /></span>
                                    {translate('ABB.Powertrain.Frontend.crossAssetClearAll')}
                                </span>,
                            onOptionSelect: onClear
                        }]}
                    />
                </div>
            </div>

            <div className='signal-bucket-list'>
                {/* Locked-signal selector shown in scatter/histogram when comparison set has items */}
                {isScatterOrHistogram && lockedSignalKey && totalSelectedSignals > 0 &&
                    <div className='signal-bucket-locked-selector'>
                        <span className='signal-bucket-locked-label'>
                            {translate('ABB.Powertrain.Frontend.crossAssetSignalComparedAcrossAssets') || 'Signal compared across all assets'}
                        </span>
                        <GeneralSelect
                            className='signal-bucket-locked-select'
                            inputId='signal-bucket-locked-select'
                            options={lockedSignalOptions}
                            getOptionValue={(option) => option.key}
                            getOptionLabel={(option) => option.label}
                            value={selectedLockedSignalOption}
                            onChange={(option) => onChangeLockedSignal?.(option?.key)}
                            isDisabled={!hasActiveSubscription}
                            isSearchable={false}
                            menuShouldScrollIntoView={false}
                        />
                    </div>
                }
                {/* ON GRAPH section — always rendered */}
                <div className='signal-bucket-group-label'>
                    <span>{translate('ABB.Powertrain.Frontend.crossAssetOnGraphLabel') || 'ON GRAPH'} – {signalCount}/{CHART_MAX_KPI_LIMIT}</span>
                    {renderInfoIcon(onGraphTooltip)}
                </div>
                {onGraphSignals.map((item, index) => renderItem(item, index, 'on'))}

                {/* OFF GRAPH section — always rendered */}
                <div className='signal-bucket-group-label signal-bucket-group-label--off'>
                    <span>{translate('ABB.Powertrain.Frontend.crossAssetOffGraphLabel') || 'OFF GRAPH'}</span>
                    {renderInfoIcon(offGraphTooltip)}
                </div>
                {offGraphSignals?.map((item, index) => renderItem(item, index, 'off'))}

                {/* Blank state — shown when nothing is selected at all */}
                {isEmpty &&
                    <div className='signal-bucket-empty'>
                        {translate('ABB.Powertrain.Frontend.crossAssetBucketEmpty') ||
                            'Select a signal to add to the graph and comparison set'}
                    </div>
                }
            </div>
        </div>
    );
};

SignalBucketComponent.propTypes = {
    chartData: PropTypes.array,
    offGraphSignals: PropTypes.array,
    loadingOffGraphSignalKeys: PropTypes.instanceOf(Set),
    activeTab: PropTypes.string,
    onClear: PropTypes.func,
    onHideOnGraphSignal: PropTypes.func,
    onPromoteOffGraphSignal: PropTypes.func,
    onRemoveOffGraphSignal: PropTypes.func,
    onComparisonItemClick: PropTypes.func,
    selectedComparisonAssetId: PropTypes.string,
    selectedComparisonOverrideKey: PropTypes.string,
    getComparisonItemSelectionKey: PropTypes.func,
    lockedSignalKey: PropTypes.string,
    lockedSignalSelectValue: PropTypes.string,
    lockedSignalName: PropTypes.string,
    scatterHistogramSignals: PropTypes.array,
    hasActiveSubscription: PropTypes.bool,
    onChangeLockedSignal: PropTypes.func,
};

export default SignalBucketComponent;
